import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { RECOMMENDATIONS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal, count, addItem } = useCart();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!items.length) return;
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          lookup_key: i.lookup_key,
          quantity: i.quantity,
          design: i.name,
          size: i.size || "One",
          color: i.wash,
        })),
        origin_url: window.location.origin,
        email: email.trim() || null,
      };
      const { data } = await axios.post(`${API}/payments/checkout`, payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[65] bg-bds-bg/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
            className="fixed right-0 top-0 bottom-0 z-[66] w-full max-w-md bg-bds-surface border-l border-bds-border flex flex-col"
          >
            <div className="flex items-center justify-between p-6 border-b border-bds-border">
              <h3 className="font-serif-display text-2xl">Your Bag <span className="text-bds-muted text-lg">({count})</span></h3>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="w-10 h-10 grid place-items-center border border-bds-border hover:bg-bds-fg hover:text-bds-bg transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 ? (
                <p className="font-grotesk text-bds-muted text-center mt-16">Your bag is empty.</p>
              ) : (
                items.map((it) => (
                  <div key={it.lineId} data-testid="cart-item" className="flex gap-4">
                    <div className="w-20 h-20 bg-bds-surface border border-bds-border shrink-0">
                      <img src={it.designImg} alt={it.name} className="w-full h-full object-cover object-top" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between gap-2">
                        <p className="font-grotesk font-medium truncate">{it.name}</p>
                        <button onClick={() => removeItem(it.lineId)} data-testid="cart-remove" className="text-bds-muted hover:text-bds-accent transition-colors">
                          <Trash2 size={15} />
                        </button>
                      </div>
                      <p className="font-mono-bds text-[10px] uppercase tracking-[0.15em] text-bds-muted mt-1">
                        {it.wash}{it.size ? ` · ${it.size}` : ""}
                      </p>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center border border-bds-border">
                          <button onClick={() => updateQty(it.lineId, it.quantity - 1)} className="w-7 h-7 grid place-items-center hover:bg-bds-fg hover:text-bds-bg transition-colors">-</button>
                          <span className="w-8 text-center font-mono-bds text-sm">{it.quantity}</span>
                          <button onClick={() => updateQty(it.lineId, it.quantity + 1)} className="w-7 h-7 grid place-items-center hover:bg-bds-fg hover:text-bds-bg transition-colors">+</button>
                        </div>
                        <span className="font-serif-display text-lg">NZ${it.price * it.quantity}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}

              {items.length > 0 && (() => {
                const inCart = new Set(items.map((i) => i.lookup_key));
                const recs = RECOMMENDATIONS.filter((r) => !inCart.has(r.lookup_key)).slice(0, 2);
                if (!recs.length) return null;
                return (
                  <div data-testid="cross-sell" className="pt-6 mt-2 border-t border-bds-border">
                    <p className="font-mono-bds text-[11px] uppercase tracking-[0.25em] text-bds-accent mb-4">Complete the ritual</p>
                    <div className="space-y-3">
                      {recs.map((r) => (
                        <div key={r.lookup_key} data-testid={`rec-${r.lookup_key}`} className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-bds-surface border border-bds-border shrink-0">
                            <img src={r.img} alt={r.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-grotesk text-sm truncate">{r.name}</p>
                            <p className="font-mono-bds text-[10px] uppercase tracking-[0.12em] text-bds-muted">NZ${r.price}</p>
                          </div>
                          <button
                            onClick={() => addItem({ lookup_key: r.lookup_key, name: r.name, price: r.price, wash: r.wash, size: r.size, designImg: r.img, quantity: 1 })}
                            data-testid={`add-rec-${r.lookup_key}`}
                            className="w-9 h-9 grid place-items-center border border-bds-border hover:bg-bds-fg hover:text-bds-bg transition-colors shrink-0"
                            aria-label={`Add ${r.name}`}
                          >
                            <Plus size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {items.length > 0 && (
              <div className="p-6 border-t border-bds-border space-y-4">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email for order updates (optional)"
                  data-testid="cart-email-input"
                  className="w-full bg-transparent border-b border-bds-border focus:border-bds-fg outline-none py-3 font-grotesk text-sm placeholder:text-bds-muted transition-colors"
                />
                <div className="flex items-center justify-between">
                  <span className="font-mono-bds text-xs uppercase tracking-[0.2em] text-bds-muted">Subtotal</span>
                  <span className="font-serif-display text-2xl">NZ${subtotal}</span>
                </div>
                <p className="font-mono-bds text-[10px] text-bds-muted">Tax & shipping calculated at checkout.</p>
                <button
                  onClick={checkout}
                  disabled={loading}
                  data-testid="checkout-btn"
                  className="w-full font-mono-bds text-xs uppercase tracking-[0.2em] h-14 bg-bds-fg text-bds-bg hover:bg-bds-accent hover:text-bds-cream transition-colors disabled:opacity-50"
                >
                  {loading ? "Redirecting…" : "Checkout"}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
